import { ChatButton, type ChatPersona, useViewport } from '@tryhackme/thm-ui-components';
import { useCallback, useEffect, useMemo } from 'react';
import Draggable, { type DraggableData, type DraggableEvent } from 'react-draggable';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/app/hooks';
import * as TutorReducer from 'src/app/reducers/room/tutor.reducer';
import * as SplitScreenReducer from 'src/app/reducers/split-screen.reducer';
import { AI_TUTOR_NAME } from 'src/common/constants';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { StyledChatButtonWrapper, StyledTutor, StyledWindowWrapper } from 'src/features/room/components/tutor/tutor.styles';
import Echo from 'src/images/pngs/landing/tutor/echo.png';
import { useRoomTaskBreakdownContext } from '../task-breakdown/components/room-task-breakdown-context';
import { useDraggableTutor, useTutorEngagementPrompt, useTutorNudge, useTutorSessionInit, useTutorSocketConnectionInit, useTutorSocketEvents, useTutorTracking } from './hooks';
import { TutorWindow } from './tutor-window';
import { TutorDisplayMode } from './tutor.types';
export const Tutor = ({
  roomCode,
  roomId,
  persona = {
    name: AI_TUTOR_NAME,
    imgUrl: Echo
  }
}: {
  roomCode: string;
  roomId: string;
  persona?: ChatPersona;
}) => {
  const {
    isTaskBreakdownSupported
  } = useRoomTaskBreakdownContext();
  const {
    height: screenHeight
  } = useViewport();
  const isDarkMode = useIsDarkMode();
  const {
    chat: {
      open: isTutorOpen,
      displayMode,
      width: chatWidth,
      height: chatHeight,
      nudge,
      restarting: isRestarting,
      messages
    }
  } = useAppSelector(state => state.tutor);
  const {
    tutorPanelOpen
  } = useAppSelector(state => state.splitScreen);
  useTutorSessionInit(roomCode);
  useTutorSocketConnectionInit({
    roomId,
    roomCode
  });
  const {
    readMessages
  } = useTutorSocketEvents({
    roomId,
    roomCode
  });
  const {
    position,
    draggingState,
    bounds,
    hasDragged,
    setDraggingState,
    handleDragStart,
    handleDrag,
    handleDragStop
  } = useDraggableTutor({
    chatWidth,
    chatHeight
  });
  const {
    engagementPromptMessage,
    showEngagementPrompt,
    handleUserInteractionWithTutor
  } = useTutorEngagementPrompt({
    roomCode
  });
  const {
    clearNudge
  } = useTutorNudge({
    roomId,
    roomCode
  });
  const {
    trackTutorOpen,
    trackChatWindowDragged,
    trackNudgeOpened
  } = useTutorTracking(roomCode);
  const dispatch = useDispatch();

  /* 
    Sanity check to ensure the tutor is closed when the tutor panel is closed.
  */
  useEffect(() => {
    if (!tutorPanelOpen && displayMode === TutorDisplayMode.EMBEDDED) {
      dispatch(TutorReducer.closeTutor());
    }
  }, [tutorPanelOpen, displayMode, dispatch]);
  useEffect(() => {
    if (!screenHeight) return;
    if (screenHeight >= 800) {
      dispatch(TutorReducer.setChatHeight(75.2));
    } else if (screenHeight >= 600) {
      dispatch(TutorReducer.setChatHeight(screenHeight * 0.75 / 10));
    } else {
      dispatch(TutorReducer.setChatHeight(0));
      dispatch(TutorReducer.closeTutor());
    }
  }, [screenHeight, dispatch]);
  const handleOpenTutor = useCallback(() => {
    if (isTutorOpen) return;
    if (!isRestarting && !messages.length) {
      dispatch(TutorReducer.setIsLoading(true));
      readMessages({
        source: 'tutor-open'
      });
    }
    if (displayMode === TutorDisplayMode.EMBEDDED) {
      dispatch(SplitScreenReducer.setTutorPanelOpen(true));
    }
    dispatch(TutorReducer.openTutor());
    trackTutorOpen(displayMode);
  }, [dispatch, displayMode, trackTutorOpen, isTutorOpen, isRestarting, messages.length, readMessages]);
  const handleButtonHover = useCallback(() => {
    handleUserInteractionWithTutor();
    if (nudge.status === 'active') {
      dispatch(TutorReducer.setNudge({
        ...nudge,
        status: 'inactive'
      }));
    }
  }, [handleUserInteractionWithTutor, nudge, dispatch]);
  const handleButtonClick = useCallback(() => {
    handleOpenTutor();
    if (nudge.status !== 'idle') {
      trackNudgeOpened();
    }
    handleUserInteractionWithTutor();
    clearNudge();
  }, [handleOpenTutor, nudge, trackNudgeOpened, handleUserInteractionWithTutor, clearNudge]);
  const handleOnDrag = useCallback((event: DraggableEvent, data: DraggableData) => {
    if (!hasDragged.current) {
      trackChatWindowDragged();
      setDraggingState('dragging');
      hasDragged.current = true;
    }
    handleDrag(event, data);
  }, [hasDragged, trackChatWindowDragged, handleDrag, setDraggingState]);
  const memoizedTutorWindow = useMemo(() => <TutorWindow roomCode={roomCode} />, [roomCode]);
  const memoizedChatButton = useMemo(() => <ChatButton className="tutor-drag-handle" data-testid="chat-button-icon" persona={persona} onClick={handleButtonClick} onHover={handleButtonHover} hasActiveNudge={nudge.status === 'active'} hasInactiveNudge={nudge.status === 'inactive'} nudgeMessage={nudge.preview} hasEngagementPrompt={showEngagementPrompt} engagementPromptMessage={nudge.status === 'idle' ? engagementPromptMessage : nudge.preview} isDarkMode={isDarkMode} />, [nudge, showEngagementPrompt, engagementPromptMessage, handleButtonClick, handleButtonHover, persona, isDarkMode]);
  return <Draggable handle=".tutor-drag-handle" bounds={bounds} axis="both" onStart={handleDragStart} onDrag={handleOnDrag} onStop={handleDragStop} position={position} data-sentry-element="Draggable" data-sentry-component="Tutor" data-sentry-source-file="tutor.tsx">
      <StyledTutor data-testid="tutor" $draggingState={draggingState} data-sentry-element="StyledTutor" data-sentry-source-file="tutor.tsx">
        <StyledChatButtonWrapper $tutorOpen={isTutorOpen} $isTaskBreakdownSupported={isTaskBreakdownSupported} data-sentry-element="StyledChatButtonWrapper" data-sentry-source-file="tutor.tsx">
          {!isTutorOpen && memoizedChatButton}
        </StyledChatButtonWrapper>
        {displayMode === TutorDisplayMode.FLOATING && <StyledWindowWrapper data-testid="tutor-floating-window" $tutorOpen={isTutorOpen} $draggingState={draggingState} $displayMode={displayMode} $width={chatWidth} $height={chatHeight}>
            {memoizedTutorWindow}
          </StyledWindowWrapper>}
      </StyledTutor>
    </Draggable>;
};